import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchBusinessPartners } from "../../redux/slices/businessPartnersSlice";
import { marchanticon } from "../../assets/ImageExports";
import "./marchantlist.css";
import { ErrorPage } from "../error/ErrorPage";
import { BellRing } from "lucide-react";

const statusColor = {
  Pending: "warning",
  Approved: "success",
  Rejected: "danger",
  Processing: "primary",
};

const getStatus = (member_active) => {
  if (member_active === 1) return "Approved";
  if (member_active === 2) return "Pending";
  if (member_active === 3) return "Processing";
  if (member_active === 0) return "Rejected";
  return "Pending";
};

const Mrlist = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, loading, error } = useSelector(
    (state) => state.businessPartner
  );
  const partnerlist = data.partners;

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const totalPages = Math.ceil((partnerlist?.length || 0) / itemsPerPage);

  const currentData = partnerlist?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const openModal = (company) => {
    setSelectedCompany(company);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedCompany(null);
    setShowModal(false);
  };

  useEffect(() => {
    dispatch(fetchBusinessPartners());
  }, [dispatch]);

  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "auto";
  }, [showModal]);

  return (
    <>
      {partnerlist?.length > 0 && (
        <div className="card p-4">
          <h5
            className="mb-4 mt-2"
            style={{ color: "#0065F8", fontWeight: "bold" }}
          >
            Merchant List
          </h5>

          {loading ? (
            <p>Loading merchants...</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle text-center">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Icon</th>
                    <th>Partner Name</th>
                    <th>Email</th>
                    <th>Address</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((partner, idx) => (
                    <tr key={idx}>
                      <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td>
                        <img
                          src={marchanticon}
                          alt="Icon"
                          style={{ height: 40, width: 40 }}
                        />
                      </td>
                      <td>{partner.member_contact_name || "N/A"}</td>
                      <td>{partner.member_email || "N/A"}</td>
                      <td>{partner.member_firm_address || "N/A"}</td>
                      <td>
                        <span
                          className={`badge bg-${
                            statusColor[getStatus(partner.member_active)]
                          }`}
                        >
                          {getStatus(partner.member_active)}
                        </span>
                      </td>
                      <td>
                        {getStatus(partner.member_active) === "Processing" ? (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() =>
                              navigate("/MemberDocumentProcess", {
                                state: { member: partner },
                              })
                            }
                          >
                            Process
                          </button>
                        ) : getStatus(partner.member_active) === "Approved" ? (
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => openModal(partner)}
                          >
                            View
                          </button>
                        ) : (
                          <button className="btn btn-sm btn-outline-primary">
                            <BellRing size={"15"} /> Poke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center mt-3 flex-wrap gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx}
                  className={`btn ${
                    currentPage === idx + 1
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => handlePageClick(idx + 1)}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                className="btn btn-outline-primary"
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && selectedCompany && (
        <div
          className="modal show fade"
          tabIndex="-1"
          role="dialog"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Company Details - {selectedCompany.member_firm_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <table className="table table-striped table-bordered">
                  <tbody>
                    <tr>
                      <th>Firm Address</th>
                      <td>{selectedCompany.member_firm_address}</td>
                    </tr>
                    <tr>
                      <th>Owner Name</th>
                      <td>{selectedCompany.member_contact_name}</td>
                    </tr>
                    <tr>
                      <th>Email</th>
                      <td>{selectedCompany.member_email}</td>
                    </tr>
                    <tr>
                      <th>Phone</th>
                      <td>{selectedCompany.member_mobile}</td>
                    </tr>
                    <tr>
                      <th>PAN</th>
                      <td>{selectedCompany.member_pan}</td>
                    </tr>
                    <tr>
                      <th>Aadhar</th>
                      <td>{selectedCompany.member_aadhar}</td>
                    </tr>
                    <tr>
                      <th>Account No</th>
                      <td>{selectedCompany.member_account}</td>
                    </tr>
                    <tr>
                      <th>IFSC</th>
                      <td>{selectedCompany.member_ifsc}</td>
                    </tr>
                    <tr>
                      <th>Business Type</th>
                      <td>{selectedCompany.member_business_type}</td>
                    </tr>
                    <tr>
                      <th>City</th>
                      <td>{selectedCompany.member_city}</td>
                    </tr>
                    <tr>
                      <th>State</th>
                      <td>{selectedCompany.member_state}</td>
                    </tr>
                    <tr>
                      <th>Pin Code</th>
                      <td>{selectedCompany.member_pin}</td>
                    </tr>
                    <tr>
                      <th>Service Email</th>
                      <td>{selectedCompany.member_service_emails}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <ErrorPage />}
    </>
  );
};

export default Mrlist;
